import { useCallback } from 'react';
import useSWR, { type Fetcher } from 'swr';
import { z } from 'zod';

import { dateToDateString } from '~/components/utils/dateUtils';
import { useGlobalFilterContext } from '~/contexts/globalFilterContext';
import { carsKpisByPropertyIdList } from '~/data/generated/endpoints/cars/cars';
import { parseApiResponse } from '~/data/parseApiResponse';
import { type CarInProperty, carInPropertySchema } from '~/data/schemas/car';

// `CarInProperty` comes from `~/data/schemas/car`, which as of AMP-619 is the
// generated base unmodified — the nine ticket-derived KPI numbers (null for any
// car with no tickets in the window) and `oem` (null for an OEM-less car, rather
// than the key being dropped) are all `allow_null` at source now, so there is
// nothing left to correct. The endpoint is a bare array; DRF's
// LimitOffsetPagination only wraps when a `limit` is sent and this caller sends
// none, and the view returns `Response(serializer.data)` directly anyway
// (`views/api/cars.py`).
const carsKpisSchema = z.array(carInPropertySchema);

const carsFetcher: Fetcher<
  CarInProperty[],
  readonly ['property', propertyId: string, 'cars', { startDate: Date; endDate: Date }]
> = async ([_, propertyId, , { startDate, endDate }]) => {
  const { data } = await carsKpisByPropertyIdList(propertyId, {
    start_date: dateToDateString(startDate),
    end_date: dateToDateString(endDate),
  });
  return parseApiResponse(carsKpisSchema, data, 'PropertyCarsKpis');
};

/**
 * `dateRange` overrides the global Period selection for this read alone — the elevator
 * page's Performance card owns its own picker, since a car's detail page carries no
 * global filter band. It is only ever the SWR key, so two ranges are two cache entries.
 */
export const usePropertyCarsKpis = (
  propertyId: string | null,
  { dateRange }: { dateRange?: { start: Date; end: Date } } = {},
) => {
  const {
    selectedFilters: {
      date: { start: globalStart, end: globalEnd },
    },
  } = useGlobalFilterContext();
  const start = dateRange?.start ?? globalStart;
  const end = dateRange?.end ?? globalEnd;
  const {
    data: cars,
    isLoading: loading,
    mutate,
  } = useSWR(
    propertyId
      ? (['property', propertyId, 'cars', { startDate: start, endDate: end }] as const)
      : null,
    carsFetcher,
  );
  const refresh = useCallback(async () => {
    await mutate();
  }, [mutate]);

  return { cars, loading, refresh };
};
